import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import VerifiedAvatar from './VerifiedAvatar';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useProfileLikeCount, useProfileLikeStatus, useToggleProfileLike } from '../api/profileLikes';
import { buildProfilePath } from '../utils/profileUrl';

function formatCategoryPath(categoryPath) {
  const parts = [categoryPath?.main?.name, categoryPath?.sub?.name, categoryPath?.subSub?.name].filter(Boolean);
  return parts.join(' / ');
}

export const ProfileCardSkeleton = ({ variant = 'default' } = {}) => {
  const coverPaddingTop = variant === 'promo' ? '56.25%' : '66%';

  return (
    <div className="card overflow-hidden flex flex-col profile-card-skeleton">
      <div className="relative w-full bg-secondary-100 overflow-hidden" style={{ paddingTop: coverPaddingTop }}>
        <div className="absolute inset-0 skeleton-block" />
      </div>
      <div className="p-3 flex items-center gap-3">
        <div className="skeleton-avatar" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="skeleton-line w-3/4" />
          <div className="skeleton-line w-1/2" />
        </div>
        <div className="skeleton-pill" />
      </div>
    </div>
  );
};

const ProfileCard = ({ profile, preferCover480 = false }) => {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const profileId = profile?.id;

  const tagsScrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const dragStateRef = React.useRef({
    isDown: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const profileUrl = buildProfilePath({ profile, categoryPath: profile?.categoryPath });

  // Feed card header image:
  // - On mobile screens: prefer cover_mobile_url (portrait)
  // - Otherwise: use cover_url (landscape)
  // We render via <picture> so the browser picks the correct source by viewport.
  const coverMobileUrl = profile?.cover_mobile_url || null;
  const coverDesktopUrl = profile?.cover_url || null;
  const coverDesktop480Url = profile?.cover_url_480 || null;
  const [coverFailed, setCoverFailed] = React.useState(false);
  const [coverTryIndex, setCoverTryIndex] = React.useState(0);

  const coverCandidates = React.useMemo(() => {
    const list = [];
    if (preferCover480 && coverDesktop480Url) list.push(coverDesktop480Url);

    // Default behavior: prefer mobile cover if present.
    // Landing override still keeps same fallback order after 480.
    if (coverMobileUrl) list.push(coverMobileUrl);
    if (coverDesktopUrl) list.push(coverDesktopUrl);

    // De-dupe while preserving order
    const uniq = [];
    const seen = new Set();
    for (const u of list) {
      const key = String(u);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      uniq.push(u);
    }
    return uniq;
  }, [preferCover480, coverDesktop480Url, coverMobileUrl, coverDesktopUrl]);

  // Feed behavior: ALWAYS use mobile cover if it exists (independent of viewport).
  // If mobile is missing, use desktop.
  // If image fails to load, hide it.
  const coverSrc = coverCandidates[coverTryIndex] || null;
  const isUsingMobileCover = !!(coverSrc && coverMobileUrl && String(coverSrc) === String(coverMobileUrl));
  const isUsing480Cover = !!(coverSrc && coverDesktop480Url && String(coverSrc) === String(coverDesktop480Url));
  const isMobileLandscape = isUsingMobileCover && /-mobile-landscape\.(webp|png|jpe?g|gif)(\?.*)?$/i.test(String(coverMobileUrl));
  const hasCover = coverCandidates.length > 0;
  // For feed we prefer mobile cover if present.
  // - mobile portrait: 5:6 => 120%
  // - mobile landscape: 16:9 => 56.25%
  // - desktop 480 cover: 16:9 => 56.25%
  // - desktop fallback: ~3:2 in this card => 66%
  const coverPaddingTop = isUsingMobileCover
    ? (isMobileLandscape ? '56.25%' : '120%')
    : isUsing480Cover
      ? '56.25%'
      : '66%';

  // Sync when profile changes
  React.useEffect(() => {
    setCoverFailed(false);
    setCoverTryIndex(0);
  }, [profileId, coverMobileUrl, coverDesktopUrl, coverDesktop480Url, preferCover480]);

  const { data: likeCountData } = useProfileLikeCount(profileId);
  const { data: likeStatusData } = useProfileLikeStatus(profileId, { enabled: !!user });
  const toggle = useToggleProfileLike(profileId);

  const count = likeCountData?.count ?? 0;
  const liked = !!likeStatusData?.liked;
  const isOwnProfile = !!user && String(user.id) === String(profileId);
  const canLike = !!user && !isOwnProfile;

  const titleLine = String(profile?.title || '').trim() || `@${String(profile?.username || '').trim()}`;
  const sloganLine = String(profile?.slogan || '').trim();
  const subtitleFallback = profile?.bio ? profile.bio : (profile?.location || formatCategoryPath(profile?.categoryPath) || '');
  const subtitleLine = sloganLine || subtitleFallback;

  const tags = React.useMemo(() => {
    const raw = Array.isArray(profile?.tags) ? profile.tags : [];
    const cleaned = raw
      .map((t) => String(t || '').trim())
      .filter(Boolean);

    // De-dupe, preserve order
    const seen = new Set();
    const out = [];
    for (const t of cleaned) {
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }, [profile?.tags]);

  const hasTags = tags.length > 0;

  const recomputeTagScrollButtons = React.useCallback(() => {
    const el = tagsScrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    // Use small epsilon to avoid flicker due to subpixel rounding
    const epsilon = 2;
    setCanScrollLeft(el.scrollLeft > epsilon);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon);
  }, []);

  React.useEffect(() => {
    if (!hasTags) return;

    // Recompute after mount/layout
    const raf = window.requestAnimationFrame(() => recomputeTagScrollButtons());

    const onResize = () => recomputeTagScrollButtons();
    window.addEventListener('resize', onResize);

    const el = tagsScrollRef.current;
    if (el) {
      el.addEventListener('scroll', recomputeTagScrollButtons, { passive: true });
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      if (el) el.removeEventListener('scroll', recomputeTagScrollButtons);
    };
  }, [hasTags, recomputeTagScrollButtons, tags]);

  const onTagsMouseDown = (e) => {
    // Only left click
    if (e.button !== 0) return;
    const el = tagsScrollRef.current;
    if (!el) return;
    dragStateRef.current.isDown = true;
    dragStateRef.current.startX = e.pageX;
    dragStateRef.current.startScrollLeft = el.scrollLeft;
    dragStateRef.current.moved = false;
  };

  const onTagsMouseMove = (e) => {
    const el = tagsScrollRef.current;
    if (!el) return;
    if (!dragStateRef.current.isDown) return;

    e.preventDefault();
    const dx = e.pageX - dragStateRef.current.startX;
    if (Math.abs(dx) > 5) dragStateRef.current.moved = true;
    el.scrollLeft = dragStateRef.current.startScrollLeft - dx;
  };

  const stopTagsDrag = () => {
    dragStateRef.current.isDown = false;
  };

  const onTagClickCapture = (e) => {
    // When user dragged horizontally, avoid accidental navigation.
    if (dragStateRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const scrollTagsBy = (dx) => {
    const el = tagsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: 'smooth' });
  };

  return (
    <div className="card overflow-hidden flex flex-col">
      {/* Cover (reserve space to avoid CLS) */}
      {hasCover && (
        <Link to={profileUrl} className="block over-bild">
          <div
            className="relative w-full bg-secondary-100 overflow-hidden"
            style={{ paddingTop: coverPaddingTop }}
          >
            {coverSrc && !coverFailed ? (
              <img
                src={getImageUrl(coverSrc)}
                alt={titleLine ? titleLine + '- изображение' : 'изображение'}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                onError={() => {
                  // Try next candidate (e.g. 480 -> mobile -> desktop). If none left, show placeholder.
                  const next = coverTryIndex + 1;
                  if (next < coverCandidates.length) {
                    setCoverTryIndex(next);
                  } else {
                    setCoverFailed(true);
                  }
                }}
              />
            ) : (
              <div className="absolute inset-0 w-full h-full bg-secondary-100" />
            )}
          </div>
        </Link>
      )}

      {/* Info */}
      <div className="p-3 flex items-center gap-3">
        <Link to={profileUrl} className="flex">
          <VerifiedAvatar
            user={profile}
            userId={profile?.id}
            src={profile?.avatar_url}
            alt={profile?.username}
            size={44}
            fallbackText={(profile?.username || '?').charAt(0)}
            badgePositionClass="bottom-0 right-0"
          />
        </Link>

        <div className="min-w-0 flex-1">
          <Link to={profileUrl} className="font-semibold text-secondary-900 truncate block leading-tight">
            {titleLine}
          </Link>
          <div className="text-xs text-secondary-600 truncate">
            {subtitleLine}
          </div>
        </div>

        <button
          type="button"
          disabled={isOwnProfile || toggle.isLoading}
          onClick={() => {
            if (!user) {
              openAuthModal('login');
              return;
            }
            if (!canLike) return;
            toggle.mutate();
          }}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-sm transition ${liked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-secondary-200 text-secondary-700'
            } ${(!user || isOwnProfile) ? 'opacity-60' : 'hover:bg-secondary-50'}`}
          title={!canLike ? 'Login erforderlich' : (liked ? 'Unlike' : 'Like')}
        >
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
          <span>{count}</span>
        </button>
      </div>

      {/* Tags (horizontal overflow) */}
      {hasTags && (
        <div className="profile-card-tags-wrap">
          {canScrollLeft && (
            <button
              type="button"
              className="profile-card-tags-arrow profile-card-tags-arrow--left"
              aria-label="Scroll tags left"
              onClick={() => scrollTagsBy(-180)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              className="profile-card-tags-arrow profile-card-tags-arrow--right"
              aria-label="Scroll tags right"
              onClick={() => scrollTagsBy(180)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          <div
            className="profile-card-tags"
            ref={tagsScrollRef}
            onMouseDown={onTagsMouseDown}
            onMouseMove={onTagsMouseMove}
            onMouseUp={stopTagsDrag}
            onMouseLeave={stopTagsDrag}
            role="list"
            aria-label="Tags"
          >
            {tags.map((tag) => (
              <Link
                key={tag}
                to={`/tag/${encodeURIComponent(tag)}`}
                className="profile-card-tag"
                title={`#${tag}`}
                role="listitem"
                onClickCapture={onTagClickCapture}
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCard;
